// Show Fingerprint (contract D.4.2, C.5): up to five meters, each a
// percentile within the band (0-100). A null axis is left out. The why lines
// come from the server's deterministic evidence templates; a line that names
// a performance gets a play button, which calls onPlayPerf(perfId).
//
// Props: { axes, why, onPlayPerf(perfId) }. `bandName` and `playingPerf` are
// optional extras (tooltip text and a spinner on the line being fetched).

import React from 'react';
import { IconPlay } from '../icons';
import './vibe.css';

export const AXES = [
  { key: 'stretch', label: 'Stretch', hint: 'How far songs ran past their usual length' },
  { key: 'flow', label: 'Flow', hint: 'How often a song went into the next one' },
  { key: 'deepCuts', label: 'Deep Cuts', hint: 'How rare the songs were' },
  { key: 'space', label: 'Space', hint: 'How much of the night was free-form space' },
  { key: 'crowd', label: 'Crowd Love', hint: 'Ratings and downloads' },
];

const clampPct = (v) => Math.max(0, Math.min(100, Math.round(v)));

export const presentAxes = (axes) =>
  AXES.filter((a) => axes && typeof axes[a.key] === 'number' && Number.isFinite(axes[a.key]));

export default function Fingerprint({ axes, why, onPlayPerf, bandName, playingPerf }) {
  const shown = presentAxes(axes);
  const lines = (Array.isArray(why) ? why : []).filter((w) => w && w.text);
  if (!shown.length && !lines.length) return null;
  const compared = 'Compared with other ' + (bandName ? bandName + ' shows' : 'shows by this band');

  return (
    <div className="vb-fp">
      {shown.length > 0 && (
        <div className="vb-meters" title={compared}>
          {shown.map((a) => {
            const v = clampPct(axes[a.key]);
            return (
              <div
                key={a.key}
                className="vb-meter"
                role="img"
                aria-label={a.label + ': ' + v + ' of 100. ' + a.hint + '. ' + compared + '.'}
              >
                <span className="vb-meter-name" title={a.hint}>
                  {a.label}
                </span>
                <span className="vb-meter-track" aria-hidden="true">
                  <span className="vb-meter-fill" style={{ width: v + '%', display: 'block' }} />
                </span>
                <span className="vb-meter-val mono" aria-hidden="true">
                  {v}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {lines.length > 0 && (
        <ul className="vb-why">
          {lines.map((w, i) => (
            <li key={(w.perfId || w.axis || '') + ':' + i}>
              <span className="vb-why-text">{w.text}</span>
              {w.perfId && onPlayPerf && (
                <button
                  type="button"
                  className="icon-btn vb-icon-sm"
                  title="Play this"
                  aria-label={'Play: ' + w.text}
                  onClick={() => onPlayPerf(w.perfId)}
                  aria-busy={playingPerf === w.perfId}
                >
                  {playingPerf === w.perfId ? <span className="spinner vb-spin" aria-hidden="true" /> : <IconPlay size={13} />}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
